// src/components/useractivity/useractivity.js
import React, { useEffect, useState, useRef } from "react";
import apiService from "../../services/apiService";
import Pagination from "../pagination/Pagination";
import { FaFilter } from "react-icons/fa";

function UserActivity() {
  const [activities, setActivities] = useState([]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [filters, setFilters] = useState({
    method: "",
    uri: "",
    userName: "",
    statusCode: "",
    startDate: "",
    endDate: "",
  });
  const [users, setUsers] = useState([]);
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const dropdownRef = useRef(null);
  const [selectedUser, setSelectedUser] = useState(null);

  useEffect(() => {
    const fetchActivities = async () => {
      try {
        console.log("Fetching activities...", { currentPage, limit });
        console.log("this is the current page:", currentPage); // Debug log
        console.log("this is the limit:", limit); // Debug log

        // Send parameters in the correct order for the backend
        const queryParams = new URLSearchParams({
          page: String(currentPage),
          limit: String(limit),
        });

        // Add filters matching the backend's UserActivityFilter struct
        if (filters.method) queryParams.append("method", filters.method);
        if (filters.uri) queryParams.append("uri", filters.uri);
        if (filters.userId) queryParams.append("userId", filters.userId);
        if (filters.statusCode)
          queryParams.append("statusCode", parseInt(filters.statusCode));
        if (filters.startDate) {
          const startDate = new Date(filters.startDate);
          queryParams.append("accessTime", startDate.toISOString());
        }
        if (filters.requestId)
          queryParams.append("requestID", filters.requestId);

        console.log("Query params:", queryParams.toString()); // Debug log

        const response = await apiService.get(
          `/api/user-activity?${queryParams.toString()}`
        );

        console.log("Response:", response.data);

        const { Items, TotalCount, TotalPages } = response.data;

        setActivities(Items || []);
        setTotalRecords(TotalCount);
        setTotalPages(TotalPages);
        setLoading(false);
      } catch (err) {
        console.error("Error fetching activities:", err);
        setError("Failed to fetch user activities");
        setLoading(false);
      }
    };

    fetchActivities();
  }, [currentPage, limit, filters]);

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  const handleLimitChange = (newLimit) => {
    setLimit(parseInt(newLimit));
    setCurrentPage(1);
  };

  // Handle filter change
  const handleFilterChange = (e) => {
    const { name, value } = e.target;

    let processedValue = value;
    if ((name === "userId" || name === "statusCode") && value !== "") {
      processedValue = parseInt(value) || ""; // Convert to integer for userName and statusCode
    }

    setFilters((prev) => ({
      ...prev,
      [name]: processedValue,
    }));
    setCurrentPage(1); // Reset to first page when filter changes
  };

  const getMethodColor = (method) => {
    const colors = {
      GET: "bg-blue-500",
      POST: "bg-green-500",
      PUT: "bg-yellow-500",
      DELETE: "bg-red-500",
    };
    return colors[method] || "bg-gray-500";
  };

  const getStatusColor = (status) => {
    if (status < 300) return "text-green-600";
    if (status < 400) return "text-blue-600";
    if (status < 500) return "text-yellow-600";
    return "text-red-600";
  };

  // Add this function to format the date
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: false, // Use 24-hour format
    });
  };

  const fetchAllUsers = async () => {
    try {
      const response = await apiService.get("/api/users");

      if (response.data && response.data.data) {
        setUsers(response.data.data);
        console.log("Fetched users:", response.data.data);
        setShowUserDropdown(true);
      } else {
        console.error("Unexpected response structure:", response.data);
      }
    } catch (err) {
      console.error("Error fetching users:", err);
    }
  };

  const handleUserSelect = (userId) => {
    setFilters((prev) => ({ ...prev, userId }));
    setSelectedUser(userId);
    setShowUserDropdown(false);
    setCurrentPage(1);
  };

  // Handle filter icon click
  const handleUserFilterClick = () => {
    fetchAllUsers();
    setShowUserDropdown((prev) => !prev);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowUserDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [dropdownRef]);

  if (loading) {
    return <div className="text-center p-4">Loading...</div>;
  }

  if (error) {
    return <div className="text-red-500 text-center p-4">{error}</div>;
  }

  return (
    <div className="container mx-auto p-4">
      <div className="flex justify-between items-center mb-4">
        <h2
          className="text-2xl font-bold mb-4"
          style={{ color: "rgb(34, 82, 130)" }}
        >
          User Activity
        </h2>
      </div>

      {/* Filters */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
        <input
          type="text"
          name="method"
          placeholder="Filter by Method"
          value={filters.method}
          onChange={handleFilterChange}
          className="p-2 border rounded"
        />
        <input
          type="text"
          name="uri"
          placeholder="Filter by URI"
          value={filters.uri}
          onChange={handleFilterChange}
          className="p-2 border rounded"
        />
        <div className="relative">
          <input
            type="number"
            name="statusCode"
            placeholder="Filter by Status Code"
            value={filters.statusCode}
            onChange={handleFilterChange}
            className="p-2 border rounded"
          />
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                Time
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                Method
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                URI
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                User ID
                <button onClick={handleUserFilterClick} className="ml-2">
                  <FaFilter />
                </button>
                {showUserDropdown && (
                  <div
                    ref={dropdownRef}
                    className="absolute z-10 bg-white border border-gray-300 rounded mt-1 w-48 max-w-full"
                  >
                    {users.map((user) => (
                      <div
                        key={user.ID}
                        onClick={() => handleUserSelect(user.ID)}
                        className="p-2 hover:bg-gray-200 cursor-pointer text-sm"
                      >
                        {user.username}
                      </div>
                    ))}
                  </div>
                )}
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                Status
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                Request ID
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {activities.map((activity) => (
              <tr key={activity.id} className="hover:bg-gray-50">
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {formatDate(activity.accessTime)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span
                    className={`px-2 py-1 text-xs font-semibold rounded-full text-white ${getMethodColor(
                      activity.method
                    )}`}
                  >
                    {activity.method}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {activity.uri}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {activity.userId}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span
                    className={`text-sm font-semibold ${getStatusColor(
                      activity.statusCode
                    )}`}
                  >
                    {activity.statusCode}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {activity.requestId}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        totalRecords={totalRecords}
        onPageChange={handlePageChange}
        onLimitChange={handleLimitChange}
        limit={limit}
      />

      {error && (
        <div className="fixed bottom-4 right-4 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
          {error}
        </div>
      )}
    </div>
  );
}

export default UserActivity;
